import { call } from '@advanza/api'
import { useAppSelector } from 'hooks/hooks'
import { useInterval } from 'hooks/miscHooks'
import { useCallback, useEffect, useState } from 'react'

export const BILLING_RUN_STEPS = {
    GENERATE_INVOICES: 'generate_invoices',
    SEND_MAILS: 'send_mails',
    CREATE_TRANSACTIONS: 'create_transactions',
} as const

export type BillingRunStepName = (typeof BILLING_RUN_STEPS)[keyof typeof BILLING_RUN_STEPS]

export type BillingRunStatus = 'running' | 'finished' | 'failed' | 'aborted'

/** Mirrors Api\Model\Entity\BillingRunStep. */
export interface BillingRunStep {
    billing_run_step_id: number
    step: BillingRunStepName
    about_month: string
    countries: string
    status: BillingRunStatus
    total: number
    processed: number
    succeeded: number
    failed: number
    skipped: number
    triggered_by: number | null
    started: string
    finished: string | null
    last_heartbeat: string | null
    is_stale: boolean
    triggered_by_user?: { id: number; username: string } | null
}

/** Mirrors Api\Model\Entity\BillingRunStepError (with contained refs). */
export interface BillingRunStepError {
    billing_run_step_error_id: number
    billing_run_step_id: number
    invoice_id: number | null
    service_provider_id: number | null
    reason_code: string
    message: string | null
    created: string
    invoice?: { invoice_id: number; invoice_nr: string; amount_netto: number } | null
    provider?: {
        service_provider_id: number
        business_name: string
        country_code: string
    } | null
}

export interface AuditInvoiceRow {
    invoiceId: number
    invoiceNr: string
    serviceProviderId: number
    businessName: string | null
    countryCode: string | null
    amountNetto: number
    reason?: string
    transactionStatus?: string
    transactionCreated?: string | null
}

/** @see BillingRunsController::reconcileQueued() */
export interface ReconcileQueuedResult {
    invoiceId: number
    invoiceNr: string | null
    outcome: 'adopted' | 'cleared' | 'error'
    transactionId: string | null
    transactionStatus: string | null
    message: string | null
}

export interface AuditChargesBucket {
    count: number
    totalAmount: number
    rows: {
        serviceProviderId: number
        businessName: string
        nrCharges: number
        totalAmount: number
    }[]
}

/** @see BillingRunsController::getAudit() */
export interface BillingRunAudit {
    uninvoicedCharges: {
        directDebit: AuditChargesBucket
        manualTransfer: AuditChargesBucket
        missingPaymentInfo: AuditChargesBucket
    }
    missingTransactions: { count: number; rows: AuditInvoiceRow[] }
    unmailedInvoices: { count: number; rows: AuditInvoiceRow[] }
    failedTransactions: { count: number; rows: AuditInvoiceRow[] }
    stuckQueuedTransactions: { count: number; rows: AuditInvoiceRow[] }
}

const POLL_INTERVAL_MS = 5000

function getErrorMessage(rejection: unknown): string {
    if (typeof rejection === 'object' && rejection !== null && 'error' in rejection) {
        return String((rejection as { error: unknown }).error)
    }

    return 'request failed'
}

/**
 * Run history + step triggers for the billing run page. Polls while
 * a step is running so the progress counters update live.
 *
 * The countries come from the office-wide country flags (left sidebar):
 * they scope what a started run works on.
 *
 * @see BillingRunsController::start()
 * @see BillingRunsController::getList()
 */
export function useBillingRuns(month: string) {
    const { countries } = useAppSelector((state) => state.countries)
    const [runs, setRuns] = useState<BillingRunStep[]>([])
    const [isFetching, setIsFetching] = useState(false)
    const [startError, setStartError] = useState<string | null>(null)

    const fetchRuns = useCallback(async () => {
        try {
            const response = await call('office/billing-runs/get-list')
            setRuns(response.runs)
        } catch (rejection) {
            // list polling failures are transient; keep the last known state
            console.error(rejection)
        }
    }, [])

    useEffect(() => {
        setIsFetching(true)
        fetchRuns().finally(() => setIsFetching(false))
    }, [fetchRuns])

    const hasRunningStep = runs.some((run) => run.status === 'running')
    useInterval(fetchRuns, hasRunningStep ? POLL_INTERVAL_MS : null)

    const startStep = async (step: BillingRunStepName) => {
        setStartError(null)
        try {
            await call('office/billing-runs/start', {
                method: 'post',
                payload: { step, month, countries },
            })
        } catch (rejection) {
            setStartError(getErrorMessage(rejection))
        }
        await fetchRuns()
    }

    const abortRun = async (runId: number) => {
        try {
            await call(`office/billing-runs/abort/${runId}`, { method: 'post' })
        } catch (rejection) {
            setStartError(getErrorMessage(rejection))
        }
        await fetchRuns()
    }

    return { runs, isFetching, startStep, abortRun, startError, countries }
}

/**
 * Error report of one run, grouped by reason code.
 *
 * @see BillingRunsController::getDetail()
 */
export function useBillingRunDetail(runId: number | null) {
    const [errors, setErrors] = useState<BillingRunStepError[]>([])
    const [countsByReason, setCountsByReason] = useState<Record<string, number>>({})
    const [isFetching, setIsFetching] = useState(false)

    useEffect(() => {
        if (runId === null) {
            setErrors([])
            setCountsByReason({})
            return
        }

        let isStale = false
        setIsFetching(true)
        call(`office/billing-runs/get-detail/${runId}`)
            .then((response) => {
                if (!isStale) {
                    setErrors(response.errors)
                    setCountsByReason(response.errorCountsByReason)
                }
            })
            .catch((rejection) => console.error(rejection))
            .finally(() => !isStale && setIsFetching(false))

        return () => {
            isStale = true
        }
    }, [runId])

    return { errors, countsByReason, isFetching }
}

/**
 * Live audit overview for a month, scoped to the office-wide country flags.
 *
 * @see BillingRunsController::getAudit()
 */
export function useBillingRunAudit(month: string, enabled: boolean) {
    const { countries } = useAppSelector((state) => state.countries)
    const [audit, setAudit] = useState<BillingRunAudit | null>(null)
    const [isFetching, setIsFetching] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Key on the joined string: the countries array gets a fresh identity on
    // every render of the page (it is derived from the URL), which would
    // otherwise retrigger the fetch effect in a loop.
    const countriesKey = countries.join(',')
    const fetchAudit = useCallback(async () => {
        setIsFetching(true)
        setError(null)
        try {
            const response = await call(`office/billing-runs/get-audit/${month}`, {
                query: { countries: countriesKey },
            })
            setAudit(response)
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        } finally {
            setIsFetching(false)
        }
    }, [month, countriesKey])

    useEffect(() => {
        if (enabled && month) {
            fetchAudit()
        }
    }, [enabled, month, fetchAudit])

    const [isReconciling, setIsReconciling] = useState(false)
    const [reconcileResults, setReconcileResults] = useState<ReconcileQueuedResult[] | null>(null)

    /**
     * Resolve invoices stuck in 'queued' without a transaction id by asking
     * Mollie whether the payment exists.
     *
     * @see BillingRunsController::reconcileQueued()
     */
    const reconcileQueued = async () => {
        setIsReconciling(true)
        setReconcileResults(null)
        try {
            const response = await call('office/billing-runs/reconcile-queued', {
                method: 'post',
                payload: { month, countries },
            })
            setReconcileResults(response.results)
            // Only on success: fetchAudit() resets the shared error state,
            // which would wipe a reconcile failure message before render.
            await fetchAudit()
        } catch (rejection) {
            setError(getErrorMessage(rejection))
        } finally {
            setIsReconciling(false)
        }
    }

    return {
        audit,
        isFetching,
        error,
        refresh: fetchAudit,
        reconcileQueued,
        isReconciling,
        reconcileResults,
    }
}
